/**
 * Converts HSL to hex.
 *
 * @param h Hue in degrees (0-360)
 * @param s Saturation in percentage (0-100)
 * @param l Lightness in percentage (0-100)
 */
const hslToHex = (h, s, l) => {
    const sNorm = s / 100;
    const lNorm = l / 100;
    const k = (n) => (n + h / 30) % 12;
    const a = sNorm * Math.min(lNorm, 1 - lNorm);
    const f = (n) => Math.round(255 * (lNorm - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
    return `#${[f(0), f(8), f(4)].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};
/**
 * Shortest distance between two hues on the colour wheel, in degrees.
 *
 * @param h1 First hue in degrees.
 * @param h2 Second hue in degrees.
 */
const hueDistance = (h1, h2) => {
    const d = Math.abs(h1 - h2);
    return Math.min(d, 360 - d);
};
/**
 * Converts a hex colour to HSL.
 *
 * @param hex Hex colour string (e.g. `#aabbcc`).
 */
const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
                h = 0;
                break;
        }
        h *= 60;
    }
    return { h, s: s * 100, l: l * 100 };
};
/**
 * Generates a distinct vibrant hex color that is not too close to existing colors.
 *
 * @param existingHexColors An array of existing hex colors to avoid.
 * @param minHueDistance Minimum hue distance to ensure distinctness (default is 30 degrees).
 */
export const getDistinctVibrantColor = (existingHexColors, minHueDistance = 30) => {
    let attempts = 0;
    while (attempts < 100) {
        attempts += 1;
        const hue = Math.floor(Math.random() * 360);
        const saturation = 70 + Math.random() * 20; // 70–90%
        const lightness = 50 + Math.random() * 10; // 50–60%
        const isDistinct = existingHexColors.every((existing) => {
            const existingHue = hexToHsl(existing).h;
            return hueDistance(hue, existingHue) >= minHueDistance;
        });
        if (isDistinct) {
            return hslToHex(hue, saturation, lightness);
        }
    }
    // Fallback if all attempts fail
    return hslToHex(Math.floor(Math.random() * 360), 75, 55);
};
